import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouterProviderProps } from 'react-router-dom';

import { PrivateRoute } from './components/PrivateRoute.js';
import { RootLayout } from './components/RootLayout.js';
import { AgencyLandingGate } from './features/agency/AgencyLandingGate';
import { PendingAccessGate } from './features/auth/PendingAccessGate';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { ClientUploadPage } from './pages/public/ClientUploadPage.js';
import { RegisterPage } from './pages/RegisterPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';

const SystemMapPage = lazy(async () => {
  const mod = await import('./pages/dev/SystemMapPage.js');
  return { default: mod.default };
});

const EmployeesPage = lazy(async () => {
  const mod = await import('./pages/employees/EmployeesPage.js');
  return { default: mod.EmployeesPage };
});

const EmployeeDetailPage = lazy(async () => {
  const mod = await import('./pages/employees/EmployeeDetailPage.js');
  return { default: mod.EmployeeDetailPage };
});

const EmployeeProfilePage = lazy(async () => {
  const mod = await import('./pages/employees/EmployeeProfilePage.js');
  return { default: mod.EmployeeProfilePage };
});

const LeavePage = lazy(async () => {
  const mod = await import('./pages/leave/LeavePage.js');
  return { default: mod.LeavePage };
});

const AttendancePage = lazy(async () => {
  const mod = await import('./pages/attendance/AttendancePage.js');
  return { default: mod.AttendancePage };
});

const PayrollPage = lazy(async () => {
  const mod = await import('./pages/payroll/PayrollPage.js');
  return { default: mod.PayrollPage };
});

const PayslipDetailPage = lazy(async () => {
  const mod = await import('./pages/payroll/PayslipDetailPage.js');
  return { default: mod.PayslipDetailPage };
});

const PerformancePage = lazy(async () => {
  const mod = await import('./pages/performance/PerformancePage.js');
  return { default: mod.PerformancePage };
});

const RecruitmentPage = lazy(async () => {
  const mod = await import('./pages/recruitment/RecruitmentPage.js');
  return { default: mod.RecruitmentPage };
});

const CareersPageBuilder = lazy(async () => {
  const mod = await import('./pages/recruitment/CareersPageBuilder.js');
  return { default: mod.default };
});

const ExpensesPage = lazy(async () => {
  const mod = await import('./pages/expenses/ExpensesPage.js');
  return { default: mod.ExpensesPage };
});

const AssetsPage = lazy(async () => {
  const mod = await import('./pages/assets/AssetsPage.js');
  return { default: mod.AssetsPage };
});

const HelpdeskPage = lazy(async () => {
  const mod = await import('./pages/helpdesk/HelpdeskPage.js');
  return { default: mod.HelpdeskPage };
});

const OnboardingPage = lazy(async () => {
  const mod = await import('./pages/onboarding/OnboardingPage.js');
  return { default: mod.OnboardingPage };
});

const ExitPage = lazy(async () => {
  const mod = await import('./pages/exit/ExitPage.js');
  return { default: mod.ExitPage };
});

const EngagementPage = lazy(async () => {
  const mod = await import('./pages/engagement/EngagementPage.js');
  return { default: mod.EngagementPage };
});

// Agency client workspace (2026-09-26). The old seven-tab AgencyPage is gone
// from the routes; its old URLs redirect to /agency, which forwards by role.
const AgencyHomePage = lazy(async () => {
  const mod = await import('./pages/agency/workspace/AgencyHomePage.js');
  return { default: mod.AgencyHomePage };
});
const AgencyCommitmentsStandalonePage = lazy(async () => {
  const mod = await import('./pages/agency/workspace/AgencyHomePage.js');
  return { default: mod.AgencyCommitmentsStandalonePage };
});
const AdminClientsPage = lazy(async () => {
  const mod = await import('./pages/agency/workspace/AdminClientsPage.js');
  return { default: mod.AdminClientsPage };
});
const AgencySettingsPage = lazy(async () => {
  const mod = await import('./pages/agency/workspace/AgencySettingsPage.js');
  return { default: mod.AgencySettingsPage };
});
const MyClientsPage = lazy(async () => {
  const mod = await import('./pages/agency/workspace/MyClientsPage.js');
  return { default: mod.MyClientsPage };
});
const AiEditorPage = lazy(async () => {
  const mod = await import('./pages/agency/workspace/AiEditorPage.js');
  return { default: mod.AiEditorPage };
});
const ClientWorkspacePage = lazy(async () => {
  const mod = await import('./pages/agency/workspace/ClientWorkspacePage.js');
  return { default: mod.ClientWorkspacePage };
});
const MyTasksPage = lazy(async () => {
  const mod = await import('./pages/agency/workspace/MyTasksPage.js');
  return { default: mod.MyTasksPage };
});

const TrainingPage = lazy(async () => {
  const mod = await import('./pages/training/TrainingPage.js');
  return { default: mod.TrainingPage };
});

const VendorsPage = lazy(async () => {
  const mod = await import('./pages/vendors/VendorsPage.js');
  return { default: mod.VendorsPage };
});

const CompliancePage = lazy(async () => {
  const mod = await import('./pages/compliance/CompliancePage.js');
  return { default: mod.CompliancePage };
});

const WhistleblowerPage = lazy(async () => {
  const mod = await import('./pages/whistleblower/WhistleblowerPage.js');
  return { default: mod.WhistleblowerPage };
});

const WorkforcePage = lazy(async () => {
  const mod = await import('./pages/workforce/WorkforcePage.js');
  return { default: mod.WorkforcePage };
});

const DeiPage = lazy(async () => {
  const mod = await import('./pages/dei/DeiPage.js');
  return { default: mod.DeiPage };
});

const DocumentsPage = lazy(async () => {
  const mod = await import('./pages/documents/DocumentsPage.js');
  return { default: mod.DocumentsPage };
});

const SubscriptionPage = lazy(async () => {
  const mod = await import('./pages/subscription/SubscriptionPage.js');
  return { default: mod.SubscriptionPage };
});

const LifecyclePage = lazy(async () => {
  const mod = await import('./pages/lifecycle/LifecyclePage.js');
  return { default: mod.LifecyclePage };
});

const SuperAdminPage = lazy(async () => {
  const mod = await import('./pages/admin/SuperAdminPage.js');
  return { default: mod.SuperAdminPage };
});

const RolesPage = lazy(async () => {
  const mod = await import('./pages/admin/RolesPage.js');
  return { default: mod.RolesPage };
});

const ReportsPage = lazy(async () => {
  const mod = await import('./pages/reports/ReportsPage.js');
  return { default: mod.ReportsPage };
});

const OrgPage = lazy(async () => {
  const mod = await import('./pages/org/OrgPage.js');
  return { default: mod.OrgPage };
});

const ESSPage = lazy(async () => {
  const mod = await import('./pages/ess/ESSPage.js');
  return { default: mod.ESSPage };
});

const DashboardPage = lazy(async () => {
  const mod = await import('./pages/dashboard/DashboardPage.js');
  return { default: mod.DashboardPage };
});

const AICopilotPage = lazy(async () => {
  const mod = await import('./pages/ai/AICopilotPage.js');
  return { default: mod.AICopilotPage };
});

const CompensationPage = lazy(async () => {
  const mod = await import('./pages/compensation/CompensationPage.js');
  return { default: mod.CompensationPage };
});

const CompanySettingsPage = lazy(async () => {
  const mod = await import('./pages/settings/CompanySettingsPage.js');
  return { default: mod.CompanySettingsPage };
});

const ContractsPage = lazy(async () => {
  const mod = await import('./pages/contracts/ContractsPage.js');
  return { default: mod.ContractsPage };
});

const NotificationsPage = lazy(async () => {
  const mod = await import('./pages/notifications/NotificationsPage.js');
  return { default: mod.NotificationsPage };
});

const OmbudsmanPage = lazy(async () => import('./pages/public/OmbudsmanPage.js'));

const CareersPage = lazy(async () => import('./pages/public/CareersPage.js'));

function PageFallback(): ReactNode {
  return (
    <div className="text-muted-foreground flex min-h-[40vh] items-center justify-center text-sm">
      Loading…
    </div>
  );
}

export const router: RouterProviderProps['router'] = createBrowserRouter([
  {
    /*
     * Top level, OUTSIDE RootLayout — not merely outside the authenticated
     * branch.
     *
     * This is the one page in the product whose audience is a CLIENT, not a
     * member of staff. Nested under RootLayout it inherited the application
     * shell, so a signed-in staff member opening the link to check it saw the
     * whole HR sidebar — Payroll, Employees, Compensation — wrapped around a
     * page meant to say "send us your photos". A client would not see it
     * (RootLayout renders the shell only when authenticated), but "it only
     * leaks the chrome to people who already have it" is a weak defence for a
     * page that should never have had a shell at all.
     *
     * The token in the path IS the credential, and the endpoint behind it is
     * upload-only.
     */
    path: 'upload/:token',
    element: (
      <Suspense fallback={<PageFallback />}>
        <ClientUploadPage />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> }, // AgencyLandingGate on /dashboard forwards agency tenants from here too
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      {
        path: 'report/:tenantSlug',
        element: (
          <Suspense fallback={<PageFallback />}>
            <OmbudsmanPage />
          </Suspense>
        ),
      },
      {
        path: 'careers/:tenantSlug',
        element: (
          <Suspense fallback={<PageFallback />}>
            <CareersPage />
          </Suspense>
        ),
      },
      {
        path: 'dev/system-map',
        element: (
          <Suspense fallback={<PageFallback />}>
            <SystemMapPage />
          </Suspense>
        ),
      },
      {
        element: <PrivateRoute />,
        children: [
          {
            path: 'dashboard',
            element: (
              <AgencyLandingGate>
                <PendingAccessGate>
                  <Suspense fallback={<PageFallback />}>
                    <DashboardPage />
                  </Suspense>
                </PendingAccessGate>
              </AgencyLandingGate>
            ),
          },
          {
            path: 'ess',
            element: (
              <Suspense fallback={<PageFallback />}>
                <ESSPage />
              </Suspense>
            ),
          },
          {
            path: 'employees',
            element: (
              <Suspense fallback={<PageFallback />}>
                <EmployeesPage />
              </Suspense>
            ),
          },
          {
            path: 'employees/:id',
            element: (
              <Suspense fallback={<PageFallback />}>
                <EmployeeDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'employees/:id/profile',
            element: (
              <Suspense fallback={<PageFallback />}>
                <EmployeeProfilePage />
              </Suspense>
            ),
          },
          {
            path: 'org',
            element: (
              <Suspense fallback={<PageFallback />}>
                <OrgPage />
              </Suspense>
            ),
          },
          {
            path: 'leave',
            element: (
              <Suspense fallback={<PageFallback />}>
                <LeavePage />
              </Suspense>
            ),
          },
          {
            path: 'attendance',
            element: (
              <Suspense fallback={<PageFallback />}>
                <AttendancePage />
              </Suspense>
            ),
          },
          {
            path: 'payroll',
            element: (
              <Suspense fallback={<PageFallback />}>
                <PayrollPage />
              </Suspense>
            ),
          },
          {
            path: 'payroll/payslips/:id',
            element: (
              <Suspense fallback={<PageFallback />}>
                <PayslipDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'performance',
            element: (
              <Suspense fallback={<PageFallback />}>
                <PerformancePage />
              </Suspense>
            ),
          },
          {
            path: 'recruitment',
            element: (
              <Suspense fallback={<PageFallback />}>
                <RecruitmentPage />
              </Suspense>
            ),
          },
          {
            path: 'recruitment/careers-page',
            element: (
              <Suspense fallback={<PageFallback />}>
                <CareersPageBuilder />
              </Suspense>
            ),
          },
          {
            path: 'expenses',
            element: (
              <Suspense fallback={<PageFallback />}>
                <ExpensesPage />
              </Suspense>
            ),
          },
          {
            path: 'assets',
            element: (
              <Suspense fallback={<PageFallback />}>
                <AssetsPage />
              </Suspense>
            ),
          },
          {
            path: 'helpdesk',
            element: (
              <Suspense fallback={<PageFallback />}>
                <HelpdeskPage />
              </Suspense>
            ),
          },
          {
            path: 'onboarding',
            element: (
              <Suspense fallback={<PageFallback />}>
                <OnboardingPage />
              </Suspense>
            ),
          },
          {
            path: 'exit',
            element: (
              <Suspense fallback={<PageFallback />}>
                <ExitPage />
              </Suspense>
            ),
          },
          {
            path: 'engagement',
            element: (
              <Suspense fallback={<PageFallback />}>
                <EngagementPage />
              </Suspense>
            ),
          },
          ...(
            [
              ['agency', AgencyHomePage],
              ['agency/clients', AdminClientsPage],
              ['agency/clients/:clientId', ClientWorkspacePage],
              ['agency/clients/:clientId/editor', AiEditorPage],
              ['agency/my-clients', MyClientsPage],
              ['agency/tasks', MyTasksPage],
              ['agency/commitments', AgencyCommitmentsStandalonePage],
              ['agency/settings', AgencySettingsPage],
            ] as const
          ).map(([path, Page]) => ({
            path,
            element: (
              <Suspense fallback={<PageFallback />}>
                <Page />
              </Suspense>
            ),
          })),
          // Old job-board URLs (bookmarks, notification links) land on the new home.
          ...['agency/board', 'agency/leads', 'agency/intake', 'agency/deliverables'].map(
            (path) => ({ path, element: <Navigate to="/agency" replace /> }),
          ),
          {
            path: 'training',
            element: (
              <Suspense fallback={<PageFallback />}>
                <TrainingPage />
              </Suspense>
            ),
          },
          {
            path: 'vendors',
            element: (
              <Suspense fallback={<PageFallback />}>
                <VendorsPage />
              </Suspense>
            ),
          },
          {
            path: 'compliance',
            element: (
              <Suspense fallback={<PageFallback />}>
                <CompliancePage />
              </Suspense>
            ),
          },
          {
            path: 'whistleblower',
            element: (
              <Suspense fallback={<PageFallback />}>
                <WhistleblowerPage />
              </Suspense>
            ),
          },
          {
            path: 'workforce',
            element: (
              <Suspense fallback={<PageFallback />}>
                <WorkforcePage />
              </Suspense>
            ),
          },
          {
            path: 'dei',
            element: (
              <Suspense fallback={<PageFallback />}>
                <DeiPage />
              </Suspense>
            ),
          },
          {
            path: 'documents',
            element: (
              <Suspense fallback={<PageFallback />}>
                <DocumentsPage />
              </Suspense>
            ),
          },
          {
            path: 'subscription',
            element: (
              <Suspense fallback={<PageFallback />}>
                <SubscriptionPage />
              </Suspense>
            ),
          },
          {
            path: 'lifecycle',
            element: (
              <Suspense fallback={<PageFallback />}>
                <LifecyclePage />
              </Suspense>
            ),
          },
          {
            path: 'admin',
            element: (
              <Suspense fallback={<PageFallback />}>
                <SuperAdminPage />
              </Suspense>
            ),
          },
          {
            path: 'admin/roles',
            element: (
              <Suspense fallback={<PageFallback />}>
                <RolesPage />
              </Suspense>
            ),
          },
          {
            path: 'reports',
            element: (
              <Suspense fallback={<PageFallback />}>
                <ReportsPage />
              </Suspense>
            ),
          },
          {
            path: 'ai-copilot',
            element: (
              <Suspense fallback={<PageFallback />}>
                <AICopilotPage />
              </Suspense>
            ),
          },
          {
            path: 'compensation',
            element: (
              <Suspense fallback={<PageFallback />}>
                <CompensationPage />
              </Suspense>
            ),
          },
          {
            path: 'settings/company',
            element: (
              <Suspense fallback={<PageFallback />}>
                <CompanySettingsPage />
              </Suspense>
            ),
          },
          {
            path: 'contracts',
            element: (
              <Suspense fallback={<PageFallback />}>
                <ContractsPage />
              </Suspense>
            ),
          },
          {
            path: 'notifications',
            element: (
              <Suspense fallback={<PageFallback />}>
                <NotificationsPage />
              </Suspense>
            ),
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
